import { supabase } from "../supabase/supabaseClient";
import SessionManager, {
  getCachedLessons,
  setCachedLessons,
  isLessonCacheFresh,
} from "../SessionManager";
import { fetchLessonCountAndLimit } from "./fetchLessonCount";

/* ---------------------------------------------------------------------
   📦 FETCH LESSONS (cached)
------------------------------------------------------------------------ */
export const fetchLessons = async ({
  authUser,
  authReady,
  setLoadingLessons,
  setErrorLessons,
  force = false,
} = {}) => {
  if (!authUser?.id || !authReady) return [];

  if (!force && isLessonCacheFresh()) {
    const cached = getCachedLessons();
    if (Array.isArray(cached)) return cached;
  }

  setLoadingLessons?.(true);

  try {
    const { data, error } = await supabase
      .from("saved_lessons")
      .select("*")
      .eq("user_id", authUser.id)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const list = (data || []).map((l) => ({
      ...l,
      jpCollapsed: true,
      rawCollapsed: true,
      vocabCollapsed: true,
    }));

    console.log("📚 fetchLessons synced:", list.length);

    setCachedLessons(list);
    SessionManager.setSavedLessons(list);

    return list;
  } catch (err) {
    console.error("❌ fetchLessons failed:", err);
    setErrorLessons?.(err.message);
    return [];
  } finally {
    setLoadingLessons?.(false);
  }
};

/* -------------------------------------------------------------------
   🧩 saveLesson — insert + cache sync
------------------------------------------------------------------- */
/* -------------------------------------------------------------------
   🧩 saveLesson — insert + cache sync
------------------------------------------------------------------- */

export async function saveLesson({
  supabase,
  authUser,
  authReady,
  showToast,

  title = "",
  english_content = "",
  japanese_content = "",
  raw_text = "",
  vocab_list = [],

  /*
   * Preserve the explanation language
   * used when the lesson was generated.
   */
  lang_code = "en",
}) {
  if (
    !authReady ||
    !authUser?.id
  ) {
    showToast?.(
      "⚠️ Please log in before saving."
    );

    return null;
  }

  /* =========================================================
     Lesson limit
  ========================================================= */

  const stats =
    await fetchLessonCountAndLimit(
      supabase
    );

  if (!stats) {
    showToast?.(
      "⚠️ Unable to verify lesson limit."
    );

    return null;
  }

  const {
    count,
    limit,
  } = stats;

  if (
    limit === 0 ||
    (
      Number.isFinite(
        limit
      ) &&
      count >= limit
    )
  ) {
    showToast?.(
      "📚 Lesson limit reached. Upgrade to save more."
    );

    window
      .openPlansModal
      ?.();

    return null;
  }

  /* =========================================================
     Normalize language
  ========================================================= */

  const normalizedLangCode =
    String(
      lang_code ||
        "en"
    )
      .trim()
      .toLowerCase();

  /* =========================================================
     Build payload
  ========================================================= */

  const payload = {
    user_id:
      authUser.id,

    title:
      title?.trim() ||
      `Lesson - ${new Date().toLocaleDateString()}`,

    english_content:
      english_content
        ?.trim() ||
      "",

    japanese_content:
      japanese_content
        ?.trim() ||
      "",

    raw_text:
      raw_text
        ?.trim() ||
      "",

    vocab_list:
      Array.isArray(
        vocab_list
      )
        ? vocab_list
        : (() => {
            try {
              return JSON.parse(
                vocab_list ||
                  "[]"
              );
            } catch {
              return [];
            }
          })(),

    /*
     * IMPORTANT:
     * This was previously missing.
     */
    lang_code:
      normalizedLangCode,
  };

  if (
    !payload.english_content &&
    !payload.japanese_content &&
    !payload.raw_text
  ) {
    showToast?.(
      "⚠️ Nothing to save."
    );

    return null;
  }

  if (
    process.env.NODE_ENV !==
    "production"
  ) {
    console.log(
      "💾 saveLesson payload:",
      {
        lang_code:
          payload.lang_code,

        hasEnglish:
          !!payload
            .english_content,

        hasJapanese:
          !!payload
            .japanese_content,

        rawTextLength:
          payload
            .raw_text
            .length,
      }
    );
  }

  /* =========================================================
     Insert
  ========================================================= */

  try {
    const {
      data,
      error,
    } =
      await supabase
        .from(
          "saved_lessons"
        )
        .insert([
          payload,
        ])
        .select();

    if (error) {
      throw error;
    }

    if (
      !data?.length
    ) {
      throw new Error(
        "Empty insert response."
      );
    }

    const inserted = {
      ...data[0],

      created_at:
        data[0]
          .created_at ||
        new Date()
          .toISOString(),

      jpCollapsed:
        true,

      rawCollapsed:
        true,

      vocabCollapsed:
        true,
    };

    /* =======================================================
       Cache sync

       inserted already includes lang_code from Supabase.
    ======================================================= */

    const prev =
      getCachedLessons() ||
      [];

    const next = [
      inserted,

      ...prev.filter(
        (lesson) =>
          !String(
            lesson.id
          ).startsWith(
            "temp-"
          )
      ),
    ];

    setCachedLessons(
      next
    );

    SessionManager
      .setSavedLessons(
        next
      );

    window.dispatchEvent(
      new Event(
        "savedLessons"
      )
    );

    window.dispatchEvent(
      new Event(
        "unified-stats:updated"
      )
    );

    showToast?.(
      "✅ Lesson saved!"
    );

    return inserted;
  } catch (err) {
    console.error(
      "❌ saveLesson failed:",
      err
    );

    showToast?.(
      "❌ Save failed."
    );

    return null;
  }
}

/* -------------------------------------------------------------------
   🗑️ deleteLesson — delete + cache sync
-------------------------------------------------------------------- */
export async function deleteLesson(supabase, lessonId, userId) {
  if (!supabase || !lessonId || !userId) {
    throw new Error("deleteLesson: missing params");
  }

  const prev = getCachedLessons() || [];
  const next = prev.filter((l) => l.id !== lessonId);

  // 🧪 TEMP lesson (optimistic)
  if (String(lessonId).startsWith("temp-")) {
    setCachedLessons(next);
    SessionManager.setSavedLessons(next);

    window.dispatchEvent(new Event("savedLessons"));
    window.dispatchEvent(new Event("unified-stats:updated"));

    return true;
  }

  const { error } = await supabase
    .from("saved_lessons")
    .delete()
    .eq("id", lessonId)
    .eq("user_id", userId);

  if (error) throw error;

  // ✅ Update cache
  setCachedLessons(next);
  SessionManager.setSavedLessons(next);

  window.dispatchEvent(new Event("savedLessons"));
  window.dispatchEvent(new Event("unified-stats:updated"));

  return true;
}